import { Plus } from "lucide-react";
import { SectionHeading, ScreenshotFrame, BetaBadge, NewBadge } from "../common/ui";
import FrothAiIcon from "../common/FrothAiIcon";
import Watermark from "../common/Watermark";
import testCasesShot from "../../../assets/product/test-cases.webp";
import requirementsShot from "../../../assets/product/requirements-generate-ai.webp";
import securityShot from "../../../assets/product/security-dashboard.webp";
import kanbanShot from "../../../assets/product/task-kanban.webp";

// Numbered product chapters (Linear-style): split head, a large screenshot and a "+" feature list.
// Each feature is a native <details>, so the full text stays in the page for search engines.
const chapters = [
  {
    id: "test-management",
    label: "1.0 Test management",
    title: (
      <>
        Test smarter, <span>from requirement to release</span>
      </>
    ),
    text: "Plan, write, review and approve test scenarios and test cases across projects, with approval workflows, bulk actions, custom fields and a full audit history.",
    more: { to: "/workflow", label: "See the workflow" },
    shot: { src: testCasesShot, height: 700, alt: "Test case list with approval status, AI-generated and automation coverage" },
    features: [
      {
        title: "Requirements and traceability",
        text: "Manage requirements and child requirements, sync them with Jira, Plane or Azure DevOps, and trace every requirement to its test cases, executions and defects.",
      },
      {
        title: "Test plans and sign-off",
        text: "Build a test plan for each release, run it through review and sign-off, and download a Test Plan Summary Report with ISO 27001 controls mapping.",
      },
      {
        title: "Defect management",
        text: "Track defects from logging to closure with severity, priority and aging metrics, and sync them two-way with Jira, Plane or Azure DevOps.",
      },
      {
        title: "Manual, API and automated execution",
        text: "Run manual and automated test cycles, build data-driven REST API tests and multi-step API workflows, and execute automation on your own GitHub Actions or GitLab runners and BrowserStack devices.",
      },
    ],
  },
  {
    id: "frothai",
    label: "2.0 FrothAI",
    title: (
      <>
        AI built into <span>every stage</span>
      </>
    ),
    text: "Generate test scenarios and test cases from requirements, defects or a prompt, then let FrothAI turn approved test cases into Playwright (web) or Appium (mobile) scripts.",
    more: { to: "/froth-ai", label: "Explore FrothAI" },
    mark: <FrothAiIcon size={460} />,
    shot: { src: requirementsShot, height: 662, alt: "Generating requirements and test scenarios with FrothAI" },
    features: [
      {
        title: "Test and script generation",
        badge: <BetaBadge />,
        text: "Generate test cases from requirements, defects or a prompt, and automation scripts from approved test case steps. Or record them with the TestOps Recorder.",
      },
      {
        title: "Ask FrothAI",
        badge: <BetaBadge />,
        text: "Ask FrothAI anything about your project and get instant answers.",
      },
      {
        title: "Release readiness",
        text: "Download a Test Plan Summary Report with a FrothAI release-readiness assessment before you sign off.",
      },
      {
        title: "Defects to regression tests",
        text: "Let FrothAI turn a defect into regression test cases, so fixed bugs stay fixed.",
      },
    ],
  },
  {
    id: "test-labs",
    label: "3.0 Test Labs",
    title: (
      <>
        Performance, security and code analsyer, <span>in one report</span>
      </>
    ),
    text: "Quality is more than functional testing. Froth TestOps brings performance, security and code testing into the same platform as your test management.",
    more: { to: "/test-labs", label: "Explore Test Labs" },
    shot: { src: securityShot, height: 662, alt: "Froth TestOps Security Test Lab dashboard" },
    features: [
      {
        title: "Performance Test Lab",
        badge: <NewBadge />,
        text: "Record a user journey or upload a HAR file, auto-generate Locust load scripts and run load tests against response-time and error KPIs.",
      },
      {
        title: "Security Test Lab",
        badge: <NewBadge />,
        text: "Run OWASP ZAP security scans on demand or on a schedule, and get AI-written security reports.",
      },
      {
        title: "Code Analyzer",
        badge: <NewBadge />,
        text: "Scan your repositories for vulnerable code and leaked secrets with Semgrep and Gitleaks.",
      },
      {
        title: "AI Evaluation",
        badge: <NewBadge />,
        text: "Test the quality of your own chatbots and LLM features. An LLM judge scores every answer for relevancy, faithfulness, hallucination, toxicity and more.",
      },
    ],
  },
  {
    id: "collaboration",
    label: "4.0 Collaboration",
    title: (
      <>
        Collaboration, <span>redefined</span>
      </>
    ),
    text: "Plan QA work on a Kanban board and let FrothAI break work down into tasks. Review, approve and sign off from one Approvals Center.",
    more: { to: "/byteam", label: "Solutions by team" },
    shot: { src: kanbanShot, height: 650, alt: "Task Management Kanban board with To Do, In Progress, In Review and Done columns" },
    features: [
      {
        title: "Role permissions",
        text: "Define exactly who can do what, with role permissions that apply instantly.",
      },
      {
        title: "Project dashboard",
        text: "Track requirements, tests, automation, modules and defects on one project dashboard, and download any chart as PNG, SVG, PDF or CSV.",
      },
      {
        title: "Scheduler",
        text: "Schedule automation test cycles to run unattended, on a recurring calendar with time zones.",
      },
      {
        title: "Mobile app",
        text: "Report bugs, browse tests and stay in sync from your phone with the Froth TestOps mobile app.",
      },
    ],
  },
];

function Chapter({ id, label, title, text, more, mark, shot, features }) {
  return (
    <section className={`ft-section home-chapter${mark ? " home-chapter--mark" : ""}`} id={id} aria-labelledby={`${id}-title`}>
      {mark && (
        <Watermark className="home-chapter__mark" sway>
          {mark}
        </Watermark>
      )}
      <div className="ft-container">
        <div className="home-chapter__head">
          <span className="ft-label">{label}</span>
        </div>
        <SectionHeading title={<span id={`${id}-title`} className="home-chapter__title">{title}</span>} subtitle={text} more={more} />
        <ScreenshotFrame className="home-chapter__shot ft-frame--fade" src={shot.src} height={shot.height} alt={shot.alt} />
        <div className="home-features">
          <p className="home-features__label">Features</p>
          <div className="home-features__list">
            {features.map(f => (
              <details className="home-features__item" key={f.title}>
                <summary>
                  <span>
                    {f.title} {f.badge}
                  </span>
                  <Plus size={16} aria-hidden="true" />
                </summary>
                <p>{f.text}</p>
              </details>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Chapters() {
  return chapters.map(c => <Chapter key={c.id} {...c} />);
}
